import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router'
import { DateTime } from 'luxon'
import { useUserContext } from 'components'
import {
    Box,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableHead,
    TableRow,
    Autocomplete,
    TextField,
    Chip,
    Typography,
} from '@mui/material'
import { collections as collectionsApi } from 'services/api'
import { formatTestId } from 'src/utils/formatTestId/formatTestId'

import { useTableParams } from 'src/hooks/useTableParams'
import { TABLE_PARAMS_IDS } from 'src/constants/constants'

import styles from './collectionsActivityLog.module.scss'

const formatDate = date =>
    DateTime.fromISO(date, { zone: 'Europe/London' }).toFormat(
        'dd/LL/yyyy HH:mm',
    )

const Loading = () => {
    return (
        <Box className={styles.box}>
            <CircularProgress aria-label="Circular loading animation" />
        </Box>
    )
}

const sortAlphabetically = providers => [
    { label: 'All' },
    ...providers
        .slice()
        .map(provider => ({
            ...provider,
            label: `${provider.name} ${provider.institutionId}`,
        }))
        .sort((a, b) => {
            if (a.name > b.name) return 1
            if (a.name < b.name) return -1
            return 0
        }),
]

const ActivityLogTable = ({
    activityLogEntries,
    handleChangePage,
    handleChangeRowsPerPage,
    autoComplete,
}) => {
    return (
        <div>
            <div className={styles.actionsBar}>{autoComplete}</div>
            {!activityLogEntries?.records?.length ? (
                <div className={styles.noResults}>{'No results found.'}</div>
            ) : (
                <div>
                    <Table
                        data-test-id={formatTestId(
                            'table',
                            'collection activity log',
                        )}
                    >
                        <TableHead>
                            <TableRow className={styles.header}>
                                <TableCell className={styles.bold}>
                                    Provider ID
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Provider Name
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Submission ID
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Collection Activity ID
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Filename
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Time and date
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    User
                                </TableCell>
                                <TableCell className={styles.bold}>
                                    Activity
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activityLogEntries.records.map(
                                (logEntry, index) => (
                                    <React.Fragment key={`${logEntry.id}`}>
                                        <TableRow
                                            hover
                                            data-test-id={formatTestId(
                                                'collection activity log',
                                                `row ${index}`,
                                            )}
                                            className={styles.summaryRow}
                                        >
                                            <TableCell>
                                                {logEntry.instId}
                                            </TableCell>
                                            <TableCell>
                                                {logEntry.providerName}
                                            </TableCell>
                                            <TableCell>
                                                {logEntry.submissionUuid.slice(
                                                    -9,
                                                )}
                                            </TableCell>
                                            <TableCell>{logEntry.id}</TableCell>
                                            <TableCell>
                                                {logEntry.filename}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(
                                                    logEntry.createdUtc,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {logEntry.isAnonymised ? (
                                                    <Chip
                                                        label={
                                                            logEntry.username
                                                        }
                                                    ></Chip>
                                                ) : (
                                                    logEntry.username
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {logEntry.type.name}
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ),
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={
                            activityLogEntries.pagingMetadata
                                .totalResultsCount || 0
                        }
                        rowsPerPage={
                            activityLogEntries.pagingMetadata.limit || 10
                        }
                        page={
                            activityLogEntries.pagingMetadata.offset /
                                activityLogEntries.pagingMetadata.limit || 0
                        }
                        backIconButtonProps={{
                            'aria-label': 'previous page',
                            'data-test-id': formatTestId(
                                'previous page',
                                'collections activity log',
                            ),
                        }}
                        nextIconButtonProps={{
                            'aria-label': 'next page',
                            'data-test-id': formatTestId(
                                'next page',
                                'collections activity log',
                            ),
                        }}
                        selectProps={{
                            'data-test-id': formatTestId(
                                'open menu',
                                'page options collections activity log',
                            ),
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>
            )}
        </div>
    )
}

const STATUS_LOADING = 1
const STATUS_LOADED = 2
const STATUS_ERROR = 3

export const CollectionsActivityLog = ({ providers }) => {
    const { reference } = useParams()
    const { activeOrganisation } = useUserContext()
    const [activityLog, setActivityLog] = useState([])
    const [status, setStatus] = useState(STATUS_LOADING)

    const DEFAULT_TABLE_PARAMETERS = {
        offset: 0,
        limit: 10,
        selectedProvider: { label: 'All' },
    }

    const tableId = TABLE_PARAMS_IDS.COLLECTIONS_ACTIVITY_LOG

    const { values: tableParams, methods: tableParamsMethods } = useTableParams(
        tableId,
        DEFAULT_TABLE_PARAMETERS,
    )

    const { limit, offset, selectedProvider } = tableParams

    const { setLimit, setOffset, setSelectedProvider } = tableParamsMethods

    const instId = activeOrganisation.id

    const fetchData = useCallback(
        async ({ pageOverride, selectedProviderOverride }) => {
            try {
                const page =
                    pageOverride >= 0 ? pageOverride * limit : offset * limit
                const providerToLoad =
                    selectedProviderOverride !== undefined
                        ? selectedProviderOverride
                        : selectedProvider
                const activityLog = await collectionsApi.getActivityLog({
                    collectionReference: reference,
                    limit,
                    offset: page,
                    ...(providerToLoad?.institutionId && {
                        instId: providerToLoad.institutionId,
                    }),
                })
                activityLog.records.map(log => {
                    log.providerName =
                        providers.find(
                            provider => provider.institutionId === log.instId,
                        )?.name ?? '-'
                    return log
                })
                setActivityLog(activityLog)
                setStatus(STATUS_LOADED)
            } catch (error) {
                setStatus(STATUS_ERROR)
            }
        },
        [limit, offset, providers, reference, selectedProvider],
    )
    useEffect(() => {
        fetchData({
            pageOverride: undefined,
            selectedProviderOverride: undefined,
        })
    }, [instId, reference, providers, limit, offset, fetchData])

    const handleChangeRowsPerPage = event => {
        setLimit(event.target.value)
        setOffset(0)
    }

    const handleChangePage = (_, offset) => {
        setOffset(offset)
    }

    const handleProviderChange = event => {
        setSelectedProvider(event)
        setOffset(0)
        fetchData({ pageOverride: 0, selectedProviderOverride: event })
    }

    const autoComplete = ({ providers }) => {
        return (
            <Autocomplete
                value={selectedProvider}
                className="hdp-override"
                disablePortal
                options={sortAlphabetically(providers)}
                sx={{ width: 400 }}
                renderInput={params => (
                    <TextField
                        {...params}
                        data-test-id={formatTestId('search', 'activity log')}
                        placeholder="Filter by provider"
                    />
                )}
                onChange={(event, newValue) => handleProviderChange(newValue)}
            />
        )
    }

    const renderContent = () => {
        switch (status) {
            case STATUS_LOADING:
                return <Loading />

            case STATUS_LOADED:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <ActivityLogTable
                                activityLogEntries={activityLog}
                                handleChangeRowsPerPage={
                                    handleChangeRowsPerPage
                                }
                                handleChangePage={handleChangePage}
                                providers={providers}
                                autoComplete={autoComplete({ providers })}
                            />
                        </Grid>
                    </Grid>
                )
            case STATUS_ERROR:
                return (
                    <Box mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography>
                                    Could not load Activity Log, please contact
                                    Jisc Administrator.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )
            default:
                return null
        }
    }

    return renderContent()
}
